/**
 * Хелперы для создания Schema.org структурированных данных
 */

/**
 * Интерфейс для данных образовательной организации
 */
export interface EducationalOrganizationData {
  name?: string
  url?: string
  logo?: string
  description?: string
  address?: {
    country?: string
    locality?: string
  }
  contactPoint?: {
    telephone?: string
    email?: string
    contactType?: string
  }
  sameAs?: string[]
}

/**
 * Интерфейс для данных курса
 */
export interface CourseSchemaData {
  name: string
  description: string
  provider?: string
  url?: string
  image?: string
  lessons?: number
  courseMode?: string
  educationalLevel?: string
  isAccessibleForFree?: boolean
}

/**
 * Интерфейс для данных статьи
 */
export interface ArticleSchemaData {
  headline: string
  description: string
  author?: string
  datePublished?: string
  dateModified?: string
  image?: string
  url?: string
  publisherName?: string
  publisherLogo?: string
  /** Ключові слова (comma-separated для Schema.org) */
  keywords?: string
}

/**
 * Создает Schema.org разметку для образовательной организации
 */
export function createEducationalOrganizationSchema(
  data: Partial<EducationalOrganizationData> = {}
): Record<string, any> {
  const defaults: EducationalOrganizationData = {
    name: 'Bible School',
    url: 'https://bibleawr.com',
    logo: 'https://bibleawr.com/images/logo.png',
    description: 'Free online Bible school providing practical biblical education and spiritual growth courses',
    address: {
      country: 'UA',
      locality: 'Kyiv'
    },
    contactPoint: {
      telephone: '0 800 30 20 20',
      email: 'shkolabiblii@gmail.com',
      contactType: 'customer service'
    },
    sameAs: [
      'https://t.me/nadezhdabelarus',
      'https://t.me/hopemediaby',
      'https://youtube.com/@hopemediaby',
      'https://hope.by/',
      'https://adventist.by/',
    ]
  }

  const finalData = { ...defaults, ...data }

  return {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    "name": finalData.name,
    "url": finalData.url,
    "logo": finalData.logo,
    "description": finalData.description,
    "address": {
      "@type": "PostalAddress",
      "addressCountry": finalData.address?.country,
      "addressLocality": finalData.address?.locality
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": finalData.contactPoint?.telephone,
      "email": finalData.contactPoint?.email,
      "contactType": finalData.contactPoint?.contactType
    },
    "sameAs": finalData.sameAs
  }
}

/**
 * Создает Schema.org разметку для курса
 */
export function createCourseSchema(data: CourseSchemaData): Record<string, any> {
  const defaults = {
    provider: 'Bible School',
    courseMode: 'online',
    educationalLevel: 'beginner',
    isAccessibleForFree: true
  }

  const finalData = { ...defaults, ...data }

  return {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": finalData.name,
    "description": finalData.description,
    "provider": {
      "@type": "Organization",
      "name": finalData.provider,
      "url": "https://bibleawr.com"
    },
    "url": finalData.url,
    "image": finalData.image,
    "courseMode": finalData.courseMode,
    "educationalLevel": finalData.educationalLevel,
    "teaches": finalData.name,
    "numberOfCredits": finalData.lessons || 1,
    "isAccessibleForFree": finalData.isAccessibleForFree
  }
}

/**
 * Создает Schema.org разметку для статьи/блога
 */
export function createArticleSchema(data: ArticleSchemaData): Record<string, any> {
  const defaults = {
    author: 'Bible School',
    publisherName: 'Bible School',
    publisherLogo: 'https://bibleawr.com/images/logo.png'
  }

  const finalData = { ...defaults, ...data }

  const schema: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": finalData.headline,
    "description": finalData.description,
    "author": {
      "@type": "Organization",
      "name": finalData.author
    },
    "publisher": {
      "@type": "Organization",
      "name": finalData.publisherName,
      "logo": {
        "@type": "ImageObject",
        "url": finalData.publisherLogo
      }
    },
    "datePublished": finalData.datePublished,
    "dateModified": finalData.dateModified || finalData.datePublished,
    "image": finalData.image,
    "url": finalData.url,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": finalData.url
    }
  }
  if (finalData.keywords) {
    schema.keywords = finalData.keywords
  }
  return schema
}

/**
 * Создает Schema.org разметку для хлебных крошек
 */
export interface BreadcrumbItem {
  name: string
  url: string
}

export function createBreadcrumbSchema(breadcrumbs: BreadcrumbItem[]): Record<string, any> {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  }
}

/**
 * Создает Schema.org разметку для веб-сайта
 */
export interface WebSiteSchemaData {
  name?: string
  url?: string
  description?: string
  publisher?: string
  potentialAction?: {
    target?: string
    queryInput?: string
  }
}

export function createWebSiteSchema(data: Partial<WebSiteSchemaData> = {}): Record<string, any> {
  const defaults: WebSiteSchemaData = {
    name: 'Bible School',
    url: 'https://bibleawr.com',
    description: 'Free online Bible school with practical biblical education courses',
    publisher: 'Bible School',
    potentialAction: {
      target: 'https://bibleawr.com/search?q={search_term_string}',
      queryInput: 'required name=search_term_string'
    }
  }

  const finalData = { ...defaults, ...data }

  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": finalData.name,
    "url": finalData.url,
    "description": finalData.description,
    "publisher": {
      "@type": "Organization",
      "name": finalData.publisher
    },
    "potentialAction": {
      "@type": "SearchAction",
      "target": finalData.potentialAction?.target,
      "query-input": finalData.potentialAction?.queryInput
    }
  }
}